'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface NotificationSettings {
  priceAlerts: boolean;
  orderUpdates: boolean;
  promotions: boolean;
  newsletter: boolean;
  smsNotifications: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
}

interface NotificationSettingsSectionProps {
  initialSettings: NotificationSettings;
}

const NotificationSettingsSection = ({ initialSettings }: NotificationSettingsSectionProps) => {
  const [settings, setSettings] = useState<NotificationSettings>(initialSettings);

  const toggleSetting = (key: keyof NotificationSettings) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  const notificationOptions = [
    {
      key: 'priceAlerts' as keyof NotificationSettings,
      label: 'Price Alerts',
      description: 'Get notified when fish prices change',
      icon: 'BellAlertIcon',
    },
    {
      key: 'orderUpdates' as keyof NotificationSettings,
      label: 'Order Updates',
      description: 'Receive updates about your orders',
      icon: 'ClipboardDocumentCheckIcon',
    },
    {
      key: 'promotions' as keyof NotificationSettings,
      label: 'Promotions & Offers',
      description: 'Stay updated on special deals',
      icon: 'TagIcon',
    },
    {
      key: 'newsletter' as keyof NotificationSettings,
      label: 'Newsletter',
      description: 'Weekly fish market insights',
      icon: 'EnvelopeIcon',
    },
  ];

  const channelOptions = [
    {
      key: 'smsNotifications' as keyof NotificationSettings,
      label: 'SMS Notifications',
      icon: 'DevicePhoneMobileIcon',
    },
    {
      key: 'emailNotifications' as keyof NotificationSettings,
      label: 'Email Notifications',
      icon: 'EnvelopeIcon',
    },
    {
      key: 'pushNotifications' as keyof NotificationSettings,
      label: 'Push Notifications',
      icon: 'BellIcon',
    },
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
          <Icon name="BellIcon" size={24} className="text-accent" />
        </div>
        <div>
          <h2 className="font-heading font-semibold text-lg text-foreground">Notification Settings</h2>
          <p className="text-sm text-muted-foreground">Manage your alert preferences</p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="font-medium text-sm text-foreground mb-4">Notification Types</h3>
          <div className="space-y-3">
            {notificationOptions.map((option) => (
              <div
                key={option.key}
                className="flex items-start justify-between gap-4 p-4 rounded-lg bg-background border border-border"
              >
                <div className="flex items-start gap-3 flex-1">
                  <div className="flex items-center justify-center w-8 h-8 bg-card rounded-md mt-0.5">
                    <Icon name={option.icon as any} size={18} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-foreground mb-1">{option.label}</h4>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleSetting(option.key)}
                  className={`relative w-12 h-6 rounded-full transition-smooth ${
                    settings[option.key] ? 'bg-primary' : 'bg-muted'
                  }`}
                  aria-label={`Toggle ${option.label}`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-smooth ${
                      settings[option.key] ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-6 border-t border-border">
          <h3 className="font-medium text-sm text-foreground mb-4">Notification Channels</h3>
          <div className="space-y-3">
            {channelOptions.map((option) => (
              <div
                key={option.key}
                className="flex items-center justify-between gap-4 p-4 rounded-lg bg-background border border-border"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-card rounded-md">
                    <Icon name={option.icon as any} size={18} className="text-primary" />
                  </div>
                  <span className="font-medium text-sm text-foreground">{option.label}</span>
                </div>
                <button
                  onClick={() => toggleSetting(option.key)}
                  className={`relative w-12 h-6 rounded-full transition-smooth ${
                    settings[option.key] ? 'bg-primary' : 'bg-muted'
                  }`}
                  aria-label={`Toggle ${option.label}`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-smooth ${
                      settings[option.key] ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsSection;